/**
 * The ant GraphQL operations behind the Support Chat console.
 *
 * Every name and argument here comes from ant's SDL (priv/graphql/schema.graphql).
 * Enum-like fields (status, priority, senderType, visibility) are typed String
 * there: ant returns the atom names in lowercase ("open", "in_progress",
 * "urgent", "user", "internal") and expects them back the same way.
 */

import { IHardwareEvent } from '../../ts/interface/hardware.interface';
import {
  formatMutationErrors,
  MutationPayload,
  postGraphQL,
  unwrapMutation,
} from './client';

// ============================================================================
// Raw ant shapes
// ============================================================================

export interface AntUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

export interface AntAdminUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * The rider's conversation as the queue reads it. The rider's name and phone
 * are NOT here: `user` is a non-null User that needs user:read, so one
 * unreadable user would null the whole queue for an admin without that
 * permission. They are read apart, per conversation (fetchRiderIdentities).
 */
export interface AntChatConversation {
  id: string;
  lastMessageAt: string | null;
  lastReadAtUser: string | null;
  lastReadAtAdmin: string | null;
  userId: string;
}

/** The kinds of button an agent can attach to a reply (ant's MessageAction.kind). */
export const CHAT_ACTION_KINDS = [
  'reply',
  'open_rental',
  'retry_unlock',
  'end_rental',
  'add_payment_method',
  'open_url',
] as const;
export type ChatActionKind = typeof CHAT_ACTION_KINDS[number];

/** A button on an agent's reply, as ant stores it. `kind` is lowercase. */
export interface AntChatMessageAction {
  kind: string;
  label: string;
  value: string | null;
  rentalId: string | null;
}

/** What sendChatMessageAsAdmin takes per action (ChatMessageActionsInput). */
export interface ChatMessageActionInput {
  kind: ChatActionKind;
  label: string;
  value?: string | null;
  rentalId?: string | null;
}

/**
 * ChatMessage.media: a photo or video in the chat media bucket. `url` and
 * `posterUrl` are GET URLs ant signed (good for at least 10 more minutes);
 * both are null while media is switched off in ant. Legacy photos are not
 * here: they keep `attachmentImage`.
 */
export interface AntChatMessageMedia {
  /** "photo" or "video". */
  kind: string;
  url: string | null;
  posterUrl: string | null;
  contentType: string;
  byteSize: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
}

export interface AntChatMessage {
  id: string;
  clientMessageId: string;
  senderType: string;
  visibility: string;
  body: string | null;
  attachmentCategory: string | null;
  sentLat: number | null;
  sentLng: number | null;
  insertedAt: string;
  conversationId: string;
  sessionId: string;
  rentalId: string | null;
  contextHardwareId: string | null;
  senderUserId: string | null;
  senderAdminId: string | null;
  attachmentImageId: string | null;
  senderAdmin: AntAdminUser | null;
  attachmentImage: { id: string; url: string | null } | null;
  /**
   * What the rider's app knew when they wrote (device, app version, network,
   * permissions, BLE, lock state). Admin-only in ant; AshGraphql's Json, so
   * an object (or, defensively, its JSON text). Absent on older ant builds.
   */
  clientContext?: Record<string, unknown> | string | null;
  /** Buttons an agent attached to a reply. */
  actions?: AntChatMessageAction[] | null;
  /** How the rider sees the author: "Ana R.", "Movatic Assistant"; null for riders. */
  senderName?: string | null;
  /** A photo or video from the chat media bucket; null otherwise. */
  media?: AntChatMessageMedia | null;
}

export interface AntChatSession {
  id: string;
  status: string;
  priority: string;
  handledBy: string;
  tags: string[] | null;
  resolutionNote: string | null;
  firstResponseAt: string | null;
  closedAt: string | null;
  lastMessageAt: string | null;
  insertedAt: string;
  updatedAt: string;
  conversationId: string;
  rentalId: string | null;
  systemId: string | null;
  /**
   * Named so the queue can filter and label sessions per system; `oldId` is
   * admin_api's id, what a support ticket takes as its system; `organization`
   * is the system's organization, null when it has none or the token cannot
   * read it (see SESSION_FIELDS).
   */
  system?: {
    id: string;
    name: string | null;
    oldId?: string | null;
    organization?: { id: string; name: string | null } | null;
  } | null;
  assignedAdminId: string | null;
  conversation: AntChatConversation;
  assignedAdmin: AntAdminUser | null;
  /** Who closed the session; null while it is open. */
  closedByAdmin?: AntAdminUser | null;
  /** When and why the assistant handed the session to a person. */
  escalatedAt?: string | null;
  escalationReason?: string | null;
  /** The rider's rating of a closed session (1 to 5), once. */
  csatScore?: number | null;
  csatComment?: string | null;
  csatAt?: string | null;
  /**
   * Admin-only: the rider's first public message after Support's last public
   * reply (or their first at all), null when Support spoke last. The SLA clock.
   */
  waitingSince?: string | null;
  /**
   * Newest public message only (limit 1, newest first; notes left out), with
   * what the queue row previews of it: its text, its author's shown name and
   * whether it carried a photo or a video.
   */
  messages?: (Pick<AntChatMessage, 'id' | 'senderType' | 'visibility' | 'insertedAt'> &
    Partial<
      Pick<
        AntChatMessage,
        'body' | 'senderName' | 'attachmentCategory' | 'attachmentImageId'
      >
    > & { media?: Pick<AntChatMessageMedia, 'kind'> | null })[];
}

interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

interface Connection<T> {
  count: number | null;
  pageInfo: PageInfo;
  edges: Array<{ node: T }> | null;
}

export type ChatSessionStatus = 'open' | 'in_progress' | 'closed';

// ============================================================================
// Documents
// ============================================================================

const CONVERSATION_FIELDS = `
  fragment ChatConversationFields on ChatConversation {
    id
    lastMessageAt
    lastReadAtUser
    lastReadAtAdmin
    userId
  }
`;

/**
 * Arguments of the "newest message" probe on a session: the newest PUBLIC
 * one, so an internal note never passes for a reply (it would clear the
 * "Waiting" state and hide an unread rider message).
 */
const NEWEST_PUBLIC_MESSAGE = `filter: { visibility: { eq: "public" } }, sort: [{ field: INSERTED_AT, order: DESC }], limit: 1`;

/**
 * The session as the queue, the thread, the rider's history and the socket
 * read it.
 *
 * `system.organization` is selected inline, unlike the rider (see
 * AntChatConversation): ant's read policy on Organization is a single filter
 * check (organization:read, scoped by organization), and ant runs Ash with
 * `no_filter_static_forbidden_reads?: false`, so an admin without that
 * permission has the organization filtered out, not refused. The field is
 * nullable in the SDL, so the null stops there and the queue is untouched.
 * The rider's `user` is non-null, which is why a refusal there would null
 * the whole queue.
 */
export const SESSION_FIELDS = `
  fragment ChatSessionFields on ChatSession {
    id
    status
    priority
    handledBy
    tags
    resolutionNote
    firstResponseAt
    closedAt
    lastMessageAt
    insertedAt
    updatedAt
    conversationId
    rentalId
    systemId
    escalatedAt
    escalationReason
    csatScore
    csatComment
    csatAt
    waitingSince
    system {
      id
      name
      oldId
      organization {
        id
        name
      }
    }
    assignedAdminId
    conversation {
      ...ChatConversationFields
    }
    assignedAdmin {
      id
      firstName
      lastName
      email
    }
    closedByAdmin {
      id
      firstName
      lastName
      email
    }
    messages(${NEWEST_PUBLIC_MESSAGE}) {
      id
      senderType
      visibility
      insertedAt
      body
      senderName
      attachmentCategory
      attachmentImageId
      # ant's media calculation on ChatMessage (its feat/chat-media): an ant
      # without it fails this whole query, and the queue with it. Deploy
      # that ant before this console.
      media {
        kind
      }
    }
  }
  ${CONVERSATION_FIELDS}
`;

export const MESSAGE_FIELDS = `
  fragment ChatMessageFields on ChatMessage {
    id
    clientMessageId
    senderType
    visibility
    body
    attachmentCategory
    sentLat
    sentLng
    insertedAt
    conversationId
    sessionId
    rentalId
    contextHardwareId
    senderUserId
    senderAdminId
    attachmentImageId
    senderAdmin {
      id
      firstName
      lastName
      email
    }
    attachmentImage {
      id
      url
    }
    clientContext
    actions {
      kind
      label
      value
      rentalId
    }
    senderName
    media {
      kind
      url
      posterUrl
      contentType
      byteSize
      durationMs
      width
      height
    }
  }
`;

const QUEUE_QUERY = `
  query SupportChatQueue(
    $status: String
    $first: Int
    $after: String
    $sort: [ChatSessionSortInput]
  ) {
    chatSessionQueue(status: $status, first: $first, after: $after, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...ChatSessionFields
        }
      }
    }
  }
  ${SESSION_FIELDS}
`;

/**
 * The sessions the assistant is answering (ant's queue with handledBy
 * "assistant"). Its own document, so the people's queue never depends on
 * an ant that knows the argument.
 */
const ASSISTANT_QUEUE_QUERY = `
  query SupportChatAssistantQueue($first: Int, $after: String) {
    chatSessionQueue(handledBy: "assistant", first: $first, after: $after) {
      count
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...ChatSessionFields
        }
      }
    }
  }
  ${SESSION_FIELDS}
`;

const QUEUE_COUNT_QUERY = `
  query SupportChatQueueCount($status: String) {
    chatSessionQueue(status: $status, first: 1) {
      count
    }
  }
`;

const SESSION_QUERY = `
  query SupportChatSession($id: ID!) {
    getChatSession(id: $id) {
      ...ChatSessionFields
    }
  }
  ${SESSION_FIELDS}
`;

const HISTORY_QUERY = `
  query SupportChatHistory($id: ID!, $limit: Int) {
    getChatSession(id: $id) {
      id
      conversation {
        id
        sessions(sort: [{ field: INSERTED_AT, order: DESC }], limit: $limit) {
          ...ChatSessionFields
        }
      }
    }
  }
  ${SESSION_FIELDS}
`;

const MESSAGES_QUERY = `
  query SupportChatMessages(
    $conversationId: ID!
    $filter: ChatMessageFilterInput
    $first: Int
    $after: String
  ) {
    chatMessages(
      conversationId: $conversationId
      filter: $filter
      sort: [{ field: INSERTED_AT, order: DESC }]
      first: $first
      after: $after
    ) {
      count
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...ChatMessageFields
        }
      }
    }
  }
  ${MESSAGE_FIELDS}
`;

const SEND_AS_ADMIN_MUTATION = `
  mutation SupportChatSendAsAdmin($input: SendChatMessageAsAdminInput!) {
    sendChatMessageAsAdmin(input: $input) {
      result {
        ...ChatMessageFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${MESSAGE_FIELDS}
`;

const ADD_NOTE_MUTATION = `
  mutation SupportChatAddNote($input: AddChatNoteInput!) {
    addChatNote(input: $input) {
      result {
        ...ChatMessageFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${MESSAGE_FIELDS}
`;

const sessionMutation = (name: string, inputType: string) => `
  mutation SupportChat_${name}($id: ID!, $input: ${inputType}) {
    ${name}(id: $id, input: $input) {
      result {
        ...ChatSessionFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${SESSION_FIELDS}
`;

const CLOSE_SESSION_MUTATION = sessionMutation(
  'closeChatSession',
  'CloseChatSessionInput'
);
const ASSIGN_SESSION_MUTATION = sessionMutation(
  'assignChatSession',
  'AssignChatSessionInput'
);
const SET_PRIORITY_MUTATION = sessionMutation(
  'setChatSessionPriority',
  'SetChatSessionPriorityInput'
);
const ADD_TAG_MUTATION = sessionMutation('addChatSessionTag', 'AddChatSessionTagInput!');
const REMOVE_TAG_MUTATION = sessionMutation(
  'removeChatSessionTag',
  'RemoveChatSessionTagInput!'
);

const MARK_READ_BY_ADMIN_MUTATION = `
  mutation SupportChatMarkReadByAdmin($id: ID!) {
    markChatReadByAdmin(id: $id) {
      result {
        ...ChatConversationFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${CONVERSATION_FIELDS}
`;

const HARDWARE_EVENTS_QUERY = `
  query SupportChatHardwareEvents($filter: EventFilterInput, $first: Int, $sort: [EventSortInput]) {
    listEvents(filter: $filter, first: $first, sort: $sort) {
      edges {
        node {
          id
          createdOn
          recordedAt
          type
          message
        }
      }
    }
  }
`;

// ============================================================================
// Queries
// ============================================================================

/** ant caps a page at 100; walk a few pages so a busy queue still fits. */
const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/**
 * Sessions Customer Support still has to answer, oldest-waiting first.
 * Without a status ant returns everything not closed.
 */
export async function fetchQueue(status?: ChatSessionStatus): Promise<AntChatSession[]> {
  const sessions: AntChatSession[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { chatSessionQueue: Connection<AntChatSession> | null } =
      await postGraphQL(QUEUE_QUERY, { status, first: PAGE_SIZE, after });
    const connection = data.chatSessionQueue;
    (connection?.edges ?? []).forEach((edge) => sessions.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return sessions;
}

/** Sessions the assistant is answering, oldest-waiting first. */
export async function fetchAssistantQueue(): Promise<AntChatSession[]> {
  const sessions: AntChatSession[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { chatSessionQueue: Connection<AntChatSession> | null } =
      await postGraphQL(ASSISTANT_QUEUE_QUERY, { first: PAGE_SIZE, after });
    const connection = data.chatSessionQueue;
    (connection?.edges ?? []).forEach((edge) => sessions.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return sessions;
}

/**
 * The most recently closed sessions, newest first: one page, for browsing.
 * The queue's own order (oldest waiting first) would return the oldest
 * closed sessions, so this sorts by closedAt; ant applies the requested sort
 * ahead of the action's default one.
 */
export async function fetchClosedSessions(limit = PAGE_SIZE): Promise<AntChatSession[]> {
  const data: { chatSessionQueue: Connection<AntChatSession> | null } = await postGraphQL(
    QUEUE_QUERY,
    {
      status: 'closed',
      first: limit,
      after: null,
      sort: [{ field: 'CLOSED_AT', order: 'DESC' }],
    }
  );
  return (data.chatSessionQueue?.edges ?? []).map((edge) => edge.node);
}

const UNREAD_SUMMARY_QUERY = `
  query SupportChatUnreadSummary($first: Int, $after: String) {
    chatSessionQueue(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          conversation {
            id
            lastReadAtAdmin
          }
          messages(${NEWEST_PUBLIC_MESSAGE}) {
            id
            senderType
            visibility
            insertedAt
          }
        }
      }
    }
  }
`;

export type UnreadSummaryRow = {
  id: string;
  conversation: { id: string; lastReadAtAdmin: string | null } | null;
  messages?: Pick<AntChatMessage, 'id' | 'senderType' | 'visibility' | 'insertedAt'>[];
};

/**
 * The open queue with only what "does the rider have an unread message"
 * needs: for the sidebar badge on every page, so it stays small.
 */
export async function fetchUnreadSummary(): Promise<UnreadSummaryRow[]> {
  const rows: UnreadSummaryRow[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { chatSessionQueue: Connection<UnreadSummaryRow> | null } =
      await postGraphQL(UNREAD_SUMMARY_QUERY, { first: PAGE_SIZE, after });
    const connection = data.chatSessionQueue;
    (connection?.edges ?? []).forEach((edge) => rows.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return rows;
}

// ----------------------------------------------------------------------------
// Who the rider is, read apart from the queue
// ----------------------------------------------------------------------------

export interface RiderIdentity {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

/** A rider conversation and one of its sessions, the handle ant reads it by. */
export interface RiderRef {
  conversationId: string;
  sessionId: string;
}

/**
 * Sessions per request, one aliased getChatSession each: the first batch is
 * one viewport of rows, so the names on screen land first, the rest bigger.
 */
export const FIRST_RIDER_BATCH = 12;
export const RIDER_BATCH = 24;
/** Batches in flight at once. */
export const RIDER_CONCURRENCY = 3;

/** ant's rider fields on ChatConversation, readable without user:read. */
const RIDER_SELECTION = 'riderFirstName riderLastName riderPhone';
/** Until those fields are deployed: the rider through `user` (needs user:read). */
const USER_SELECTION = 'user { firstName lastName phone }';

/**
 * True once ant has said it does not know the rider fields: for the rest of
 * the page load every batch goes straight to `user { ... }` instead of
 * asking twice.
 */
let riderSelectionMissing = false;
const RIDER_SELECTION_MISSING = /Cannot query field/i;
/** Forgets that ant refused the rider fields (tests). */
export function forgetRiderSelection(): void {
  riderSelectionMissing = false;
}

const riderQuery = (count: number, selection: string): string => {
  const indexes = Array.from({ length: count }, (_, i) => i);
  const variables = indexes.map((i) => `$s${i}: ID!`).join(', ');
  const fields = indexes
    .map((i) => `r${i}: getChatSession(id: $s${i}) { id conversation { ${selection} } }`)
    .join('\n    ');
  return `query SupportChatRiders(${variables}) {\n    ${fields}\n  }`;
};

interface RiderRow {
  conversation: {
    riderFirstName?: string | null;
    riderLastName?: string | null;
    riderPhone?: string | null;
    user?: Pick<AntUser, 'firstName' | 'lastName' | 'phone'> | null;
  } | null;
}

async function readRiders(
  refs: RiderRef[],
  selection: string,
  pick: (conversation: NonNullable<RiderRow['conversation']>) => RiderIdentity
): Promise<Record<string, RiderIdentity>> {
  const variables: Record<string, string> = {};
  refs.forEach((ref, i) => {
    variables[`s${i}`] = ref.sessionId;
  });
  const data = await postGraphQL<Record<string, RiderRow | null>>(
    riderQuery(refs.length, selection),
    variables
  );
  const found: Record<string, RiderIdentity> = {};
  refs.forEach((ref, i) => {
    const conversation = data[`r${i}`]?.conversation;
    if (conversation) found[ref.conversationId] = pick(conversation);
  });
  return found;
}

/** One batch of the rider lookup, as it lands: its refs and what was found. */
export interface RiderBatch {
  refs: RiderRef[];
  found: Record<string, RiderIdentity>;
}

export interface FetchRiderOptions {
  /** Called as each batch lands, in whatever order the batches finish. */
  onBatch?: (batch: RiderBatch) => void;
}

const pickRiderFields = (c: NonNullable<RiderRow['conversation']>): RiderIdentity => ({
  firstName: c.riderFirstName ?? null,
  lastName: c.riderLastName ?? null,
  phone: c.riderPhone ?? null,
});
const pickUserFields = (c: NonNullable<RiderRow['conversation']>): RiderIdentity => ({
  firstName: c.user?.firstName ?? null,
  lastName: c.user?.lastName ?? null,
  phone: c.user?.phone ?? null,
});

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : String(error ?? '');

/** One batch, both ways; empty when neither read is allowed. */
async function readRiderBatch(refs: RiderRef[]): Promise<Record<string, RiderIdentity>> {
  if (!riderSelectionMissing) {
    try {
      return await readRiders(refs, RIDER_SELECTION, pickRiderFields);
    } catch (riderFieldsError) {
      if (RIDER_SELECTION_MISSING.test(errorText(riderFieldsError))) {
        riderSelectionMissing = true;
      }
    }
  }
  try {
    return await readRiders(refs, USER_SELECTION, pickUserFields);
  } catch (userError) {
    // Neither is readable with this token: these riders stay unnamed.
    return {};
  }
}

/** The refs cut into batches: one viewport first, then bigger ones. */
export function riderBatches(refs: RiderRef[]): RiderRef[][] {
  const batches: RiderRef[][] = [];
  let start = 0;
  while (start < refs.length) {
    const size = start === 0 ? FIRST_RIDER_BATCH : RIDER_BATCH;
    batches.push(refs.slice(start, start + size));
    start += size;
  }
  return batches;
}

/**
 * The rider's name and phone per conversation id, for the conversations
 * given, in the order given: the first batch is one viewport of rows, the
 * rest bigger, up to RIDER_CONCURRENCY batches in flight at once, and
 * `onBatch` hears each one as it lands so the rows can be named as they
 * come. Each batch tries ant's riderFirstName/riderLastName/riderPhone first
 * and, when that fails (not deployed yet, or refused), `user { ... }`; once
 * ant has said it does not know the rider fields, the rest of the page load
 * skips them. A batch that fails both ways is left out: those riders show
 * without a name.
 */
export async function fetchRiderIdentities(
  refs: RiderRef[],
  { onBatch }: FetchRiderOptions = {}
): Promise<Record<string, RiderIdentity>> {
  const found: Record<string, RiderIdentity> = {};
  const batches = riderBatches(refs);
  for (let start = 0; start < batches.length; start += RIDER_CONCURRENCY) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      batches.slice(start, start + RIDER_CONCURRENCY).map(async (batch) => {
        const read = await readRiderBatch(batch);
        Object.assign(found, read);
        onBatch?.({ refs: batch, found: read });
      })
    );
  }
  return found;
}

export async function fetchQueueCount(status: ChatSessionStatus): Promise<number> {
  const data = await postGraphQL<{ chatSessionQueue: { count: number | null } | null }>(
    QUEUE_COUNT_QUERY,
    { status }
  );
  return data.chatSessionQueue?.count ?? 0;
}

export async function fetchSession(sessionId: string): Promise<AntChatSession | null> {
  const data = await postGraphQL<{ getChatSession: AntChatSession | null }>(
    SESSION_QUERY,
    {
      id: sessionId,
    }
  );
  return data.getChatSession;
}

/** The other sessions of the rider behind `sessionId`, newest first. */
export async function fetchConversationHistory(
  sessionId: string,
  limit = 10
): Promise<AntChatSession[]> {
  const data = await postGraphQL<{
    getChatSession: { conversation: { sessions: AntChatSession[] } } | null;
  }>(HISTORY_QUERY, { id: sessionId, limit });
  return data.getChatSession?.conversation.sessions ?? [];
}

/**
 * The messages of one session, oldest first. chatMessages takes the
 * CONVERSATION id (ChatSession.conversationId) and is narrowed to the
 * session on ant's side, so a poll of the thread does not download the
 * rider's whole history. Read newest first and reversed: past the page cap
 * it is the oldest that are left out, never the newest.
 */
export async function fetchMessages(
  conversationId: string,
  sessionId: string
): Promise<AntChatMessage[]> {
  const newestFirst: AntChatMessage[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { chatMessages: Connection<AntChatMessage> | null } = await postGraphQL(
      MESSAGES_QUERY,
      {
        conversationId,
        filter: { sessionId: { eq: sessionId } },
        first: PAGE_SIZE,
        after,
      }
    );
    const connection = data.chatMessages;
    (connection?.edges ?? []).forEach((edge) => newestFirst.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return newestFirst.reverse();
}

const ADMIN_BY_EMAIL_QUERY = `
  query SupportChatAdminByEmail($email: String!) {
    listAdminUsers(filter: { email: { eq: $email } }, first: 1) {
      edges {
        node {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

/**
 * The signed-in admin as ant knows them (the same lookup the admin settings
 * page makes): "Assign to me" needs ant's id, which the REST login does not
 * carry.
 */
export async function fetchAdminByEmail(email: string): Promise<AntAdminUser | null> {
  const data = await postGraphQL<{
    listAdminUsers: { edges: Array<{ node: AntAdminUser }> | null } | null;
  }>(ADMIN_BY_EMAIL_QUERY, { email });
  return data.listAdminUsers?.edges?.[0]?.node ?? null;
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * ant refused a message's `attachment`: the object is missing, does not
 * match what was declared, or is not this conversation's. Uploading the
 * file again is what fixes it; other refusals are plain Errors.
 */
export class ChatAttachmentRejected extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatAttachmentRejected';
  }
}

export async function sendMessageAsAdmin(input: {
  sessionId: string;
  clientMessageId: string;
  body: string;
  /** A legacy photo: base64 PNG/JPEG of at most 2 MB. Never with `attachment`. */
  image?: string;
  /** A photo or video already in the chat media bucket (uploadChatMedia). */
  attachment?: ChatMessageAttachmentInput;
  /** Up to 4 buttons under the reply; ant validates kinds, https and rental ids. */
  actions?: ChatMessageActionInput[];
}): Promise<AntChatMessage> {
  const data = await postGraphQL<{
    sendChatMessageAsAdmin: MutationPayload<AntChatMessage>;
  }>(SEND_AS_ADMIN_MUTATION, { input });
  const errors = data.sendChatMessageAsAdmin?.errors ?? [];
  if (errors.some((e) => e.fields?.includes('attachment'))) {
    throw new ChatAttachmentRejected(formatMutationErrors(errors));
  }
  return unwrapMutation(data.sendChatMessageAsAdmin, 'sendChatMessageAsAdmin');
}

export async function addChatNote(input: {
  sessionId: string;
  clientMessageId: string;
  body: string;
}): Promise<AntChatMessage> {
  const data = await postGraphQL<{ addChatNote: MutationPayload<AntChatMessage> }>(
    ADD_NOTE_MUTATION,
    { input }
  );
  return unwrapMutation(data.addChatNote, 'addChatNote');
}

/**
 * `resolutionNote` is optional in ant: null closes without one. `tags` are
 * glossary ids ant APPENDS to the session in the same write (never removes),
 * so a colleague's tag survives the close; left out of the input when there
 * are none, so an ant without the argument still closes.
 */
export async function closeChatSession(
  sessionId: string,
  resolutionNote: string | null,
  tags: string[] = []
): Promise<AntChatSession> {
  const data = await postGraphQL<{ closeChatSession: MutationPayload<AntChatSession> }>(
    CLOSE_SESSION_MUTATION,
    { id: sessionId, input: { resolutionNote, ...(tags.length ? { tags } : {}) } }
  );
  return unwrapMutation(data.closeChatSession, 'closeChatSession');
}

export async function assignChatSession(
  sessionId: string,
  assignedAdminId: string | null
): Promise<AntChatSession> {
  const data = await postGraphQL<{ assignChatSession: MutationPayload<AntChatSession> }>(
    ASSIGN_SESSION_MUTATION,
    { id: sessionId, input: { assignedAdminId } }
  );
  return unwrapMutation(data.assignChatSession, 'assignChatSession');
}

export async function setChatSessionPriority(
  sessionId: string,
  priority: 'normal' | 'urgent'
): Promise<AntChatSession> {
  const data = await postGraphQL<{
    setChatSessionPriority: MutationPayload<AntChatSession>;
  }>(SET_PRIORITY_MUTATION, { id: sessionId, input: { priority } });
  return unwrapMutation(data.setChatSessionPriority, 'setChatSessionPriority');
}

/**
 * Puts one glossary tag on the session: ant appends it to the stored array
 * in SQL (idempotent, so a double click or two agents adding the same tag
 * both succeed) and refuses an id that is not an active glossary tag with
 * "not in the glossary or retired: <id>".
 */
export async function addChatSessionTag(
  sessionId: string,
  tag: string
): Promise<AntChatSession> {
  const data = await postGraphQL<{ addChatSessionTag: MutationPayload<AntChatSession> }>(
    ADD_TAG_MUTATION,
    { id: sessionId, input: { tag } }
  );
  return unwrapMutation(data.addChatSessionTag, 'addChatSessionTag');
}

/** Takes one tag off the session, whatever it is (a glossary id, a retired one, an old string). */
export async function removeChatSessionTag(
  sessionId: string,
  tag: string
): Promise<AntChatSession> {
  const data = await postGraphQL<{
    removeChatSessionTag: MutationPayload<AntChatSession>;
  }>(REMOVE_TAG_MUTATION, { id: sessionId, input: { tag } });
  return unwrapMutation(data.removeChatSessionTag, 'removeChatSessionTag');
}

const SESSION_HARDWARE_FAMILY_QUERY = `
  query SupportChatSessionHardwareFamily($id: ID!) {
    getChatSession(id: $id) {
      id
      hardwareFamily
    }
  }
`;

/**
 * The family of the anchor rental's unit (ant's HardwareFamily enum, uppercase:
 * "KAYAK", "BIKE", ...), read for the one session on screen so the tag picker
 * can narrow its options. Null on a general session, or when ant does not say.
 * Its own document: the queue's fragment must never load 100 rentals' hardware.
 */
export async function fetchSessionHardwareFamily(
  sessionId: string
): Promise<string | null> {
  const data = await postGraphQL<{
    getChatSession: { id: string; hardwareFamily: string | null } | null;
  }>(SESSION_HARDWARE_FAMILY_QUERY, { id: sessionId });
  return data.getChatSession?.hardwareFamily ?? null;
}

/** Takes the ant CONVERSATION id. */
export async function markChatReadByAdmin(
  conversationId: string
): Promise<AntChatConversation> {
  const data = await postGraphQL<{
    markChatReadByAdmin: MutationPayload<AntChatConversation>;
  }>(MARK_READ_BY_ADMIN_MUTATION, { id: conversationId });
  return unwrapMutation(data.markChatReadByAdmin, 'markChatReadByAdmin');
}

// ============================================================================
// Chat media (photos and video in the private bucket)
// ============================================================================

/** What a message carries once its file is in the bucket (ChatMessageAttachmentInput). */
export interface ChatMessageAttachmentInput {
  /** The objectKey requestChatMediaUpload gave. */
  objectKey: string;
  kind: 'photo' | 'video';
  contentType: string;
  byteSize: number;
  /** A video's length, at most 60000. */
  durationMs?: number | null;
  width?: number | null;
  height?: number | null;
  /** A video's poster frame: the objectKey of a poster upload. */
  posterKey?: string | null;
}

/** Where to PUT one file: the bytes as the body, with every header as given. */
export interface ChatMediaUploadSlot {
  uploadUrl: string;
  objectKey: string;
  headers: Array<{ name: string; value: string }>;
  expiresAt: string;
}

const REQUEST_MEDIA_UPLOAD_MUTATION = `
  mutation SupportChatRequestMediaUpload($input: RequestChatMediaUploadInput!) {
    requestChatMediaUpload(input: $input) {
      uploadUrl
      objectKey
      headers {
        name
        value
      }
      expiresAt
    }
  }
`;

const MEDIA_UPLOADS_QUERY = `
  query SupportChatMediaUploads {
    chatSupportStatus {
      mediaUploads
    }
  }
`;

/** ant's "could not issue an upload, try again" (its retryable `unavailable`). */
const SLOT_UNAVAILABLE = 'could not issue an upload';

/** How long to wait before the one retry of a slot ant could not issue. */
export const SLOT_RETRY_DELAY_MS = 1000;

/**
 * ant's refusals of a slot, in the agent's words. Anything else ant says
 * (a file too big, the wrong type) is already plain and passes through.
 */
const SLOT_REFUSALS: Array<[string, string]> = [
  [
    'the session is closed',
    "This conversation was closed, so it takes no more photos or video. The rider's next message opens a new one.",
  ],
  [
    'could not be found',
    'You cannot send photos or video into this conversation: it is not one you may reply to.',
  ],
  [
    'sessionId is required',
    'The console did not say which conversation the file is for. Reload the page and try again.',
  ],
  [
    'media uploads are not available',
    'Photo and video uploads are switched off right now.',
  ],
  [
    SLOT_UNAVAILABLE,
    'The server could not start the upload just now. Send again in a moment.',
  ],
];

export function slotRefusalText(message: string): string {
  const lower = message.toLowerCase();
  const plain = SLOT_REFUSALS.find(([said]) => lower.includes(said.toLowerCase()));
  return plain ? plain[1] : message;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A signed PUT for one file in the session (ant's SESSION id: an admin
 * names the session the file will be sent into and is authorized as the
 * send is; a closed session takes none). ant checks the type and size
 * against the kind's limits.
 *
 * ant's `unavailable` ("could not issue an upload, try again") is retried
 * once after a second; every refusal comes back in the agent's words
 * (slotRefusalText).
 */
export async function requestChatMediaUpload(input: {
  sessionId: string;
  kind: 'photo' | 'video' | 'poster';
  contentType: string;
  byteSize: number;
}): Promise<ChatMediaUploadSlot> {
  const ask = () =>
    postGraphQL<{ requestChatMediaUpload: ChatMediaUploadSlot | null }>(
      REQUEST_MEDIA_UPLOAD_MUTATION,
      { input }
    );
  let data: { requestChatMediaUpload: ChatMediaUploadSlot | null };
  try {
    try {
      data = await ask();
    } catch (error) {
      if (!(error instanceof Error && error.message.includes(SLOT_UNAVAILABLE)))
        throw error;
      await wait(SLOT_RETRY_DELAY_MS);
      data = await ask();
    }
  } catch (error) {
    if (error instanceof Error) throw new Error(slotRefusalText(error.message));
    throw error;
  }
  if (!data.requestChatMediaUpload) throw new Error('ant gave no upload slot');
  return data.requestChatMediaUpload;
}

/**
 * True when photos and video go through the bucket. False, and on any error
 * (an ant without the field included), photos go the legacy base64 way and
 * video is not offered.
 */
export async function fetchChatMediaUploads(): Promise<boolean> {
  try {
    const data = await postGraphQL<{
      chatSupportStatus: { mediaUploads: boolean | null } | null;
    }>(MEDIA_UPLOADS_QUERY);
    return data.chatSupportStatus?.mediaUploads === true;
  } catch (error) {
    return false;
  }
}

// ============================================================================
// Side panels
// ============================================================================

export interface SupportStats {
  totalConversations: number;
  totalOpen: number;
  totalInProgress: number;
  totalClosed: number;
}

/** Session counts by status, from the queue's `count`. */
export async function fetchSupportStats(): Promise<SupportStats> {
  const [totalOpen, totalInProgress, totalClosed] = await Promise.all([
    fetchQueueCount('open'),
    fetchQueueCount('in_progress'),
    fetchQueueCount('closed'),
  ]);
  return {
    totalConversations: totalOpen + totalInProgress + totalClosed,
    totalOpen,
    totalInProgress,
    totalClosed,
  };
}

// ----------------------------------------------------------------------------
// Rental and hardware the rider wrote about
// ----------------------------------------------------------------------------

export interface ChatContextSystem {
  id: string;
  /** admin_api's id: what the REST quick actions send as the system. */
  oldId: string | null;
  name: string | null;
  roundTrip?: boolean | null;
  /** Its rentable hardware type: "locker" is the dashboard's bike_locker mode. */
  rentableHardwareType?: { name: string | null } | null;
}

export interface ChatContextHardware {
  id: string;
  /** admin_api's id: what maintenance tickets take as hardware_id. */
  oldId: string | null;
  number: number | null;
  numberText: string | null;
  hardwareProduct: { name: string | null; hasLocationTracking?: boolean | null } | null;
  system: ChatContextSystem | null;
}

export interface ChatContextRental {
  id: string;
  oldId: string | null;
  startTime: string | null;
  endTime: string | null;
  hardware: ChatContextHardware | null;
  system: ChatContextSystem | null;
}

const CONTEXT_HARDWARE_FIELDS = `
  id
  oldId
  number
  numberText
  hardwareProduct {
    name
    hasLocationTracking
  }
  system {
    id
    oldId
    name
  }
`;

const RENTAL_CONTEXT_QUERY = `
  query SupportChatRental($id: ID!) {
    getRental(id: $id) {
      id
      oldId
      startTime
      endTime
      hardware {
        ${CONTEXT_HARDWARE_FIELDS}
      }
      system {
        id
        oldId
        name
        roundTrip
        rentableHardwareType {
          name
        }
      }
    }
  }
`;

const HARDWARE_CONTEXT_QUERY = `
  query SupportChatHardware($id: ID!) {
    getHardware(id: $id) {
      ${CONTEXT_HARDWARE_FIELDS}
    }
  }
`;

/** The rental a session (or one of its messages) names, as the panel shows it. */
export async function fetchRentalContext(
  rentalId: string
): Promise<ChatContextRental | null> {
  const data = await postGraphQL<{ getRental: ChatContextRental | null }>(
    RENTAL_CONTEXT_QUERY,
    { id: rentalId }
  );
  return data.getRental;
}

/** The hardware a message names, when no rental carries it. */
export async function fetchHardwareContext(
  hardwareId: string
): Promise<ChatContextHardware | null> {
  const data = await postGraphQL<{ getHardware: ChatContextHardware | null }>(
    HARDWARE_CONTEXT_QUERY,
    { id: hardwareId }
  );
  return data.getHardware;
}

/** A rental a reply's button can act on, as the composer's picker lists it. */
export interface RiderRental {
  id: string;
  oldId: string | null;
  startTime: string | null;
  endTime: string | null;
  hardware: {
    id: string;
    number: number | null;
    numberText: string | null;
    hardwareProduct: { name: string | null } | null;
  } | null;
  system: { id: string; name: string | null } | null;
}

/**
 * ant's own "open" (Rental :open_by_hardware_old_ids): started, not ended
 * (:end stamps duration) and not canceled. No system filter: ant keeps the
 * rentals of the systems the admin holds rental:read on and drops the rest
 * without an error, so none can also mean none this admin may see.
 */
const RIDER_ACTIVE_RENTALS_QUERY = `
  query SupportChatRiderRentals($userId: ID!, $first: Int) {
    listRentals(
      filter: {
        userId: { eq: $userId }
        duration: { isNil: true }
        canceledOn: { isNil: true }
        startTime: { isNil: false }
      }
      sort: [{ field: START_TIME, order: DESC }]
      first: $first
    ) {
      edges {
        node {
          id
          oldId
          startTime
          endTime
          hardware {
            id
            number
            numberText
            hardwareProduct {
              name
            }
          }
          system {
            id
            name
          }
        }
      }
    }
  }
`;

/** One page is plenty: a rider rarely has more than one rental open. */
const RIDER_RENTALS_LIMIT = 25;

/** The rider's active rentals, newest first. */
export async function fetchRiderActiveRentals(userId: string): Promise<RiderRental[]> {
  const data = await postGraphQL<{
    listRentals: { edges: Array<{ node: RiderRental }> | null } | null;
  }>(RIDER_ACTIVE_RENTALS_QUERY, { userId, first: RIDER_RENTALS_LIMIT });
  return (data.listRentals?.edges ?? []).map((edge) => edge.node);
}

export interface HardwareEventLogParams {
  limit?: number;
  after_created_on?: string;
  before_created_on?: string;
}

/**
 * The hardware event log the ActionPanel shows, from ant's listEvents (the
 * REST /hardware/{id}/events the POC used is gone from the dashboard).
 */
export async function getHardwareEventLogs(
  hardwareId: string,
  params: HardwareEventLogParams = {}
): Promise<{ data: IHardwareEvent[] }> {
  const conditions: Record<string, unknown>[] = [
    { hardware: { id: { eq: hardwareId } } },
  ];
  if (params.after_created_on) {
    conditions.push({ createdOn: { greaterThanOrEqual: params.after_created_on } });
  }
  if (params.before_created_on) {
    conditions.push({ createdOn: { lessThanOrEqual: params.before_created_on } });
  }

  const data = await postGraphQL<{
    listEvents: {
      edges: Array<{
        node: {
          id: string;
          createdOn: string;
          recordedAt: string;
          type: string | null;
          message: string | null;
        };
      }> | null;
    } | null;
  }>(HARDWARE_EVENTS_QUERY, {
    filter: conditions.length === 1 ? conditions[0] : { and: conditions },
    first: params.limit ?? 15,
    sort: [{ field: 'CREATED_ON', order: 'DESC' }],
  });

  return {
    data: (data.listEvents?.edges ?? []).map(({ node }) => ({
      id: node.id,
      created_on: node.createdOn,
      recorded_at: node.recordedAt,
      type: node.type ?? '',
    })),
  };
}

// ----------------------------------------------------------------------------
// Typing and presence (ephemeral: nothing here is stored)
// ----------------------------------------------------------------------------

const TYPING_MUTATION = `
  mutation SupportChatTyping($conversationId: ID!) {
    chatTyping(conversationId: $conversationId)
  }
`;

const AGENT_HEARTBEAT_MUTATION = `
  mutation SupportChatAgentHeartbeat {
    chatAgentHeartbeat
  }
`;

/**
 * Tells the rider and the other agents in the conversation (ant's
 * CONVERSATION id) that this agent is typing. True when ant published it,
 * false when it dropped it (within 2 s of this agent's previous one).
 */
export async function sendChatTyping(conversationId: string): Promise<boolean> {
  const data = await postGraphQL<{ chatTyping: boolean | null }>(TYPING_MUTATION, {
    conversationId,
  });
  return Boolean(data.chatTyping);
}

/** Marks the signed-in agent online for ant's next 2 minutes (the rider's "agents online"). */
export async function sendAgentHeartbeat(): Promise<boolean> {
  const data = await postGraphQL<{ chatAgentHeartbeat: boolean | null }>(
    AGENT_HEARTBEAT_MUTATION
  );
  return Boolean(data.chatAgentHeartbeat);
}

// ----------------------------------------------------------------------------
// The anchored rental's unlock attempts
// ----------------------------------------------------------------------------

export interface RentalUnlockHardware {
  id: string;
  number: number | null;
  numberText: string | null;
  hardwareProduct: { name: string | null } | null;
}

export interface RentalUnlocks {
  /**
   * Rental.unlocks: one entry per unit the rider could unlock, each
   * `{hardware_id, unlocks: [{id, timestamp, type: "attempt" | "success"}]}`
   * (ant's Json, keys as ant writes them), oldest first.
   */
  unlocks: unknown[] | null;
  /** Percent of attempts followed by a success; null without attempts. */
  unlocksSuccessRate: number | null;
  unlockableHardware: RentalUnlockHardware[] | null;
}

const RENTAL_UNLOCKS_QUERY = `
  query SupportChatRentalUnlocks($id: ID!) {
    getRental(id: $id) {
      id
      unlocks
      unlocksSuccessRate
      unlockableHardware {
        id
        number
        numberText
        hardwareProduct {
          name
        }
      }
    }
  }
`;

/** The unlock attempts of a rental, read apart so the rental section never waits on them. */
export async function fetchRentalUnlocks(
  rentalId: string
): Promise<RentalUnlocks | null> {
  const data = await postGraphQL<{ getRental: RentalUnlocks | null }>(
    RENTAL_UNLOCKS_QUERY,
    { id: rentalId }
  );
  return data.getRental;
}

// ----------------------------------------------------------------------------
// Saved replies (ChatSavedReply)
// ----------------------------------------------------------------------------

/** A canned reply an agent inserts from the composer. `systemId` null = every system. */
export interface AntChatSavedReply {
  id: string;
  title: string;
  body: string;
  systemId: string | null;
  system: { id: string; name: string | null } | null;
  updatedAt: string;
}

const SAVED_REPLY_FIELDS = `
  fragment ChatSavedReplyFields on ChatSavedReply {
    id
    title
    body
    systemId
    system {
      id
      name
    }
    updatedAt
  }
`;

const SAVED_REPLIES_QUERY = `
  query SupportChatSavedReplies($systemId: ID, $first: Int, $after: String) {
    chatSavedReplies(systemId: $systemId, first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...ChatSavedReplyFields
        }
      }
    }
  }
  ${SAVED_REPLY_FIELDS}
`;

const savedReplyMutation = (name: string, signature: string, args: string) => `
  mutation SupportChat_${name}(${signature}) {
    ${name}(${args}) {
      result {
        ...ChatSavedReplyFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${SAVED_REPLY_FIELDS}
`;

const CREATE_SAVED_REPLY_MUTATION = savedReplyMutation(
  'createChatSavedReply',
  '$input: CreateChatSavedReplyInput!',
  'input: $input'
);
const UPDATE_SAVED_REPLY_MUTATION = savedReplyMutation(
  'updateChatSavedReply',
  '$id: ID!, $input: UpdateChatSavedReplyInput',
  'id: $id, input: $input'
);
const ARCHIVE_SAVED_REPLY_MUTATION = savedReplyMutation(
  'archiveChatSavedReply',
  '$id: ID!',
  'id: $id'
);

/** ant caps a page of saved replies at 200. */
const SAVED_REPLY_PAGE = 200;

/**
 * The replies the agent may use, by title. With `systemId`, that system's
 * and the global ones (what the composer offers on a session there);
 * without, every reply the agent may read (the Manage dialog).
 */
export async function fetchSavedReplies(
  systemId?: string | null
): Promise<AntChatSavedReply[]> {
  const replies: AntChatSavedReply[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { chatSavedReplies: Connection<AntChatSavedReply> | null } =
      await postGraphQL(SAVED_REPLIES_QUERY, {
        systemId: systemId || null,
        first: SAVED_REPLY_PAGE,
        after,
      });
    const connection = data.chatSavedReplies;
    (connection?.edges ?? []).forEach((edge) => replies.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return replies;
}

/** `systemId` null makes a global reply (Movatic admins only). */
export async function createSavedReply(input: {
  title: string;
  body: string;
  systemId: string | null;
}): Promise<AntChatSavedReply> {
  const data = await postGraphQL<{
    createChatSavedReply: MutationPayload<AntChatSavedReply>;
  }>(CREATE_SAVED_REPLY_MUTATION, { input });
  return unwrapMutation(data.createChatSavedReply, 'createChatSavedReply');
}

/** Title and body only: ant keeps a reply's scope for good. */
export async function updateSavedReply(
  id: string,
  input: { title: string; body: string }
): Promise<AntChatSavedReply> {
  const data = await postGraphQL<{
    updateChatSavedReply: MutationPayload<AntChatSavedReply>;
  }>(UPDATE_SAVED_REPLY_MUTATION, { id, input });
  return unwrapMutation(data.updateChatSavedReply, 'updateChatSavedReply');
}

/** Takes the reply out of the picker; ant keeps the row. */
export async function archiveSavedReply(id: string): Promise<AntChatSavedReply> {
  const data = await postGraphQL<{
    archiveChatSavedReply: MutationPayload<AntChatSavedReply>;
  }>(ARCHIVE_SAVED_REPLY_MUTATION, { id });
  return unwrapMutation(data.archiveChatSavedReply, 'archiveChatSavedReply');
}

// ----------------------------------------------------------------------------
// Chat tag glossary (ChatTagCategory, ChatTag): CS's vocabulary for sessions
// ----------------------------------------------------------------------------

/**
 * A glossary tag as ant returns it. Enum fields (`appliesTo`) come as ant's
 * GraphQL enum values, uppercase ("KAYAK", "LAWN_GAME"); the console maps
 * them in pages/support/tagGlossary.ts. `slug` is what a session stores.
 */
export interface AntChatTag {
  id: string;
  slug: string;
  categoryId: string;
  label: string;
  definition: string | null;
  notes: string | null;
  appliesTo: string[];
  position: number;
  riderReportTagId: string | null;
  archivedAt: string | null;
}

/** A glossary category with its tags, retired ones included. `section` and `kind` are uppercase enums. */
export interface AntChatTagCategory {
  id: string;
  slug: string;
  section: string;
  name: string;
  description: string | null;
  notes: string | null;
  kind: string;
  position: number;
  archivedAt: string | null;
  tags: AntChatTag[];
}

/** `canEdit` is ant's own rule (in phase 1, a Movatic admin), never the console's. */
export interface AntChatTagGlossary {
  canEdit: boolean;
  categories: AntChatTagCategory[];
}

const TAG_FIELDS = `
  fragment ChatTagFields on ChatTag {
    id
    slug
    categoryId
    label
    definition
    notes
    appliesTo
    position
    riderReportTagId
    archivedAt
  }
`;

const TAG_CATEGORY_FIELDS = `
  fragment ChatTagCategoryFields on ChatTagCategory {
    id
    slug
    section
    name
    description
    notes
    kind
    position
    archivedAt
    tags {
      ...ChatTagFields
    }
  }
  ${TAG_FIELDS}
`;

const TAG_GLOSSARY_QUERY = `
  query SupportChatTagGlossary {
    chatTagGlossary {
      canEdit
      categories {
        ...ChatTagCategoryFields
      }
    }
  }
  ${TAG_CATEGORY_FIELDS}
`;

/**
 * The whole glossary (about 270 rows, retired included), rider support only,
 * sorted by ant by section, position, name. Once per page load; the
 * glossary hook keeps it.
 */
export async function fetchChatTagGlossary(): Promise<AntChatTagGlossary> {
  const data = await postGraphQL<{ chatTagGlossary: AntChatTagGlossary | null }>(
    TAG_GLOSSARY_QUERY
  );
  if (!data.chatTagGlossary) throw new Error('ant returned no glossary');
  return data.chatTagGlossary;
}

const tagCategoryMutation = (name: string, signature: string, args: string) => `
  mutation SupportChat_${name}(${signature}) {
    ${name}(${args}) {
      result {
        ...ChatTagCategoryFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${TAG_CATEGORY_FIELDS}
`;

const tagMutation = (name: string, signature: string, args: string) => `
  mutation SupportChat_${name}(${signature}) {
    ${name}(${args}) {
      result {
        ...ChatTagFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${TAG_FIELDS}
`;

const CREATE_TAG_CATEGORY_MUTATION = tagCategoryMutation(
  'createChatTagCategory',
  '$input: CreateChatTagCategoryInput!',
  'input: $input'
);
const UPDATE_TAG_CATEGORY_MUTATION = tagCategoryMutation(
  'updateChatTagCategory',
  '$id: ID!, $input: UpdateChatTagCategoryInput',
  'id: $id, input: $input'
);
const ARCHIVE_TAG_CATEGORY_MUTATION = tagCategoryMutation(
  'archiveChatTagCategory',
  '$id: ID!',
  'id: $id'
);
const RESTORE_TAG_CATEGORY_MUTATION = tagCategoryMutation(
  'restoreChatTagCategory',
  '$id: ID!',
  'id: $id'
);
const CREATE_TAG_MUTATION = tagMutation(
  'createChatTag',
  '$input: CreateChatTagInput!',
  'input: $input'
);
const UPDATE_TAG_MUTATION = tagMutation(
  'updateChatTag',
  '$id: ID!, $input: UpdateChatTagInput',
  'id: $id, input: $input'
);
const ARCHIVE_TAG_MUTATION = tagMutation('archiveChatTag', '$id: ID!', 'id: $id');
const RESTORE_TAG_MUTATION = tagMutation(
  'restoreChatTag',
  '$id: ID!, $input: RestoreChatTagInput',
  'id: $id, input: $input'
);

/** What createChatTagCategory takes. `section` and `kind` are ant's uppercase enum values. */
export interface CreateChatTagCategoryInput {
  section: string;
  name: string;
  description?: string | null;
  notes?: string | null;
  kind?: string;
  /** Defaults in ant to one more than the section's highest. */
  position?: number | null;
}

export interface UpdateChatTagCategoryInput {
  name?: string;
  description?: string | null;
  notes?: string | null;
  position?: number;
}

/** What createChatTag takes. `appliesTo` holds ant's uppercase HardwareFamily values; empty = every rental. */
export interface CreateChatTagInput {
  categoryId: string;
  label: string;
  definition?: string | null;
  notes?: string | null;
  appliesTo?: string[];
  /** Defaults in ant to one more than the category's highest. */
  position?: number | null;
  riderReportTagId?: string | null;
}

export interface UpdateChatTagInput {
  label?: string;
  definition?: string | null;
  notes?: string | null;
  appliesTo?: string[];
  position?: number;
  riderReportTagId?: string | null;
}

export async function createChatTagCategory(
  input: CreateChatTagCategoryInput
): Promise<AntChatTagCategory> {
  const data = await postGraphQL<{
    createChatTagCategory: MutationPayload<AntChatTagCategory>;
  }>(CREATE_TAG_CATEGORY_MUTATION, { input });
  return unwrapMutation(data.createChatTagCategory, 'createChatTagCategory');
}

export async function updateChatTagCategory(
  id: string,
  input: UpdateChatTagCategoryInput
): Promise<AntChatTagCategory> {
  const data = await postGraphQL<{
    updateChatTagCategory: MutationPayload<AntChatTagCategory>;
  }>(UPDATE_TAG_CATEGORY_MUTATION, { id, input });
  return unwrapMutation(data.updateChatTagCategory, 'updateChatTagCategory');
}

/** Retires the category: its tags leave the picker, untouched themselves. */
export async function archiveChatTagCategory(id: string): Promise<AntChatTagCategory> {
  const data = await postGraphQL<{
    archiveChatTagCategory: MutationPayload<AntChatTagCategory>;
  }>(ARCHIVE_TAG_CATEGORY_MUTATION, { id });
  return unwrapMutation(data.archiveChatTagCategory, 'archiveChatTagCategory');
}

export async function restoreChatTagCategory(id: string): Promise<AntChatTagCategory> {
  const data = await postGraphQL<{
    restoreChatTagCategory: MutationPayload<AntChatTagCategory>;
  }>(RESTORE_TAG_CATEGORY_MUTATION, { id });
  return unwrapMutation(data.restoreChatTagCategory, 'restoreChatTagCategory');
}

/** ant computes the slug (`<category>.<label>`) once, here; nothing changes it later. */
export async function createChatTag(input: CreateChatTagInput): Promise<AntChatTag> {
  const data = await postGraphQL<{ createChatTag: MutationPayload<AntChatTag> }>(
    CREATE_TAG_MUTATION,
    { input }
  );
  return unwrapMutation(data.createChatTag, 'createChatTag');
}

/** A tag never changes category: no categoryId here. */
export async function updateChatTag(
  id: string,
  input: UpdateChatTagInput
): Promise<AntChatTag> {
  const data = await postGraphQL<{ updateChatTag: MutationPayload<AntChatTag> }>(
    UPDATE_TAG_MUTATION,
    { id, input }
  );
  return unwrapMutation(data.updateChatTag, 'updateChatTag');
}

/** Retires the tag: out of the picker, kept on the sessions that carry it. */
export async function archiveChatTag(id: string): Promise<AntChatTag> {
  const data = await postGraphQL<{ archiveChatTag: MutationPayload<AntChatTag> }>(
    ARCHIVE_TAG_MUTATION,
    { id }
  );
  return unwrapMutation(data.archiveChatTag, 'archiveChatTag');
}

/** Brings a retired tag back, under a new label when its old one now collides with an active tag. */
export async function restoreChatTag(
  id: string,
  input?: { label?: string }
): Promise<AntChatTag> {
  const data = await postGraphQL<{ restoreChatTag: MutationPayload<AntChatTag> }>(
    RESTORE_TAG_MUTATION,
    { id, input: input ?? null }
  );
  return unwrapMutation(data.restoreChatTag, 'restoreChatTag');
}

// ----------------------------------------------------------------------------
// Systems, for pickers (saved reply scope, metrics filter)
// ----------------------------------------------------------------------------

export interface ChatSystemOption {
  id: string;
  name: string | null;
}

const SYSTEMS_QUERY = `
  query SupportChatSystems($first: Int, $after: String) {
    listSystems(first: $first, after: $after, sort: [{ field: NAME, order: ASC }]) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

/** Every system the token can read, by name (ant's own scoping). */
export async function fetchChatSystems(): Promise<ChatSystemOption[]> {
  const systems: ChatSystemOption[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data: { listSystems: Connection<ChatSystemOption> | null } = await postGraphQL(
      SYSTEMS_QUERY,
      { first: PAGE_SIZE, after }
    );
    const connection = data.listSystems;
    (connection?.edges ?? []).forEach((edge) => systems.push(edge.node));
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) break;
    after = connection.pageInfo.endCursor;
  }
  return systems;
}

// ----------------------------------------------------------------------------
// Support ticket from a chat (the existing createSupportTicket)
// ----------------------------------------------------------------------------

export interface CreateSupportTicketInput {
  /** The rider's phone: admin_api's user id. */
  userOldId: string;
  systemOldId?: string;
  rentalOldId?: string;
  content: string;
}

export interface CreatedSupportTicket {
  id: string;
  /** admin_api's ticket number, what the support-ticket page and CS call it. */
  oldId: string | null;
}

const CREATE_SUPPORT_TICKET_MUTATION = `
  mutation SupportChatCreateSupportTicket($input: CreateSupportTicketInput) {
    createSupportTicket(input: $input) {
      result {
        id
        oldId
      }
      errors {
        message
        code
        fields
      }
    }
  }
`;

export async function createSupportTicket(
  input: CreateSupportTicketInput
): Promise<CreatedSupportTicket> {
  const data = await postGraphQL<{
    createSupportTicket: MutationPayload<CreatedSupportTicket>;
  }>(CREATE_SUPPORT_TICKET_MUTATION, { input });
  return unwrapMutation(data.createSupportTicket, 'createSupportTicket');
}

// ----------------------------------------------------------------------------
// Customer Support's numbers (chatMetrics)
// ----------------------------------------------------------------------------

/** Sessions opened in [from, to) the admin may read. Times in seconds. */
export interface ChatMetrics {
  from: string;
  to: string;
  sessions: number;
  closed: number;
  medianFirstResponseSeconds: number | null;
  p90FirstResponseSeconds: number | null;
  medianResolutionSeconds: number | null;
  csatAverage: number | null;
  csatCount: number;
  byTag: Array<{ tag: string; count: number }>;
  /** UTC days ("2026-09-25"), oldest first; days without sessions are left out. */
  byDay: Array<{ day: string; sessions: number }>;
  byHandler: Array<{ handledBy: string; count: number }>;
  /** More sessions than one read covers: the numbers cover the earliest. */
  truncated: boolean;
}

const METRICS_QUERY = `
  query SupportChatMetrics($from: DateTime!, $to: DateTime!, $systemId: ID) {
    chatMetrics(from: $from, to: $to, systemId: $systemId) {
      from
      to
      sessions
      closed
      medianFirstResponseSeconds
      p90FirstResponseSeconds
      medianResolutionSeconds
      csatAverage
      csatCount
      byTag {
        tag
        count
      }
      byDay {
        day
        sessions
      }
      byHandler {
        handledBy
        count
      }
      truncated
    }
  }
`;

/**
 * The numbers for [from, to) (at most 92 days), for one system or every one
 * the admin may read. A system the admin cannot read gives zeros.
 */
export async function fetchChatMetrics(
  from: string,
  to: string,
  systemId?: string | null
): Promise<ChatMetrics> {
  const data = await postGraphQL<{ chatMetrics: ChatMetrics }>(METRICS_QUERY, {
    from,
    to,
    systemId: systemId || null,
  });
  return data.chatMetrics;
}

// ----------------------------------------------------------------------------
// The assistant's sessions
// ----------------------------------------------------------------------------

const TAKE_OVER_MUTATION = `
  mutation SupportChatTakeOver($id: ID!) {
    takeOverChatSession(id: $id) {
      result {
        ...ChatSessionFields
      }
      errors {
        message
        code
        fields
      }
    }
  }
  ${SESSION_FIELDS}
`;

/**
 * An agent takes a session from the assistant: assigned to them, handled by
 * a person from now on (on a person's session it is "assign to me").
 */
export async function takeOverChatSession(sessionId: string): Promise<AntChatSession> {
  const data = await postGraphQL<{
    takeOverChatSession: MutationPayload<AntChatSession>;
  }>(TAKE_OVER_MUTATION, { id: sessionId });
  return unwrapMutation(data.takeOverChatSession, 'takeOverChatSession');
}
